import { Avatar, avatarColor, Badge, Card } from '@parsina/framework';
import { C } from '@parsina/framework/clinical/src/constants/theme';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import {
  conversationTimeLabel, formatUnread, roleLabelKey,
} from '../../screens/Messages/Messages.shared';

// The phone's conversation row (DESIGN-CONCEPT-MOBILE v2 §1.1/§5): its own
// component, not the desktop row under a media query. Taller, no hover state,
// one tap target of at least 44px — and the same row model, from the same
// shared module, so the two surfaces can never disagree about what a row says.
//
// `dir="auto"` on the name and the preview: they are user content and each
// resolves its own direction.

const MobileConversationCard = ({ row, onOpen, testId }) => {
  const { t, formatNumber, formatTime, formatDate } = useTranslation();

  const name = row.peerDisplayName || t('messages.unknownContact');
  const timeLabel = conversationTimeLabel(row.lastActivityTs, { t, formatTime, formatDate });
  const unreadLabel = formatUnread(row.unread, formatNumber);
  const preview = row.lastEvent?.body || '';

  return (
    <Card
      onClick={() => onOpen(row)}
      data-testid={testId}
      className="min-h-11"
      style={{ padding: '0.75rem', borderRadius: '0.875rem' }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', minWidth: 0 }}>
        <Avatar name={name} size={44} bg={avatarColor(name)} />

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', minWidth: 0 }}>
            <span
              dir="auto"
              style={{
                flex: 1, minWidth: 0,
                fontSize: '0.9375rem', fontWeight: row.unread > 0 ? 800 : 700,
                color: C.textPrimary,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}
            >
              {name}
            </span>
            {timeLabel && (
              <span style={{ fontSize: '0.6875rem', color: C.textMuted, flexShrink: 0 }}>
                {timeLabel}
              </span>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.25rem', minWidth: 0 }}>
            <span
              dir="auto"
              style={{
                flex: 1, minWidth: 0,
                fontSize: '0.8125rem', color: C.textSecondary,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}
            >
              {preview || t(roleLabelKey(row))}
            </span>
            {unreadLabel && (
              // See ConversationRow: the kit's `count` variant owns the chip so
              // «۱» and «۹+» stay the same size on both surfaces.
              <Badge
                variant="count"
                data-testid={testId ? `${testId}-unread` : undefined}
                aria-label={t('messages.unreadBadge', { count: unreadLabel })}
              >
                {unreadLabel}
              </Badge>
            )}
          </div>

          {row.reachable === false && (
            <div style={{ fontSize: '0.6875rem', color: C.textMuted, marginTop: '0.25rem' }}>
              {t('messages.notOnApp')}
            </div>
          )}
          {!row.started && row.reachable !== false && (
            <div style={{ fontSize: '0.6875rem', color: C.textMuted, marginTop: '0.25rem' }}>
              {t('messages.startConversation')}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export default MobileConversationCard;
